/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmPopup } from "primereact/confirmpopup";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import { InputSwitch } from "primereact/inputswitch";

export default function Boton({
  uri,
  callback,
  children,
  disabled,
  loader,
  confirmacion,
  texto,
  icono,
  chico,
  verde,
  azul,
  rojo,
  amarillo,
  negro,
  blanco,
  transparente,
  ancho,
  interruptor,
  interruptorActivo,
  menu,
  badge,
  nuevaPestana
}) {
  const [popupConfirmacion, setPopupConfirmacion] = useState(false);
  const boton = useRef(null);
  const navigate = useNavigate();

  let clases = "";
  if (chico) {
    clases = clases + " sinMargen";
  }
  if (negro) {
    clases = clases + " negro";
  }
  if (verde) {
    clases = clases + " verde";
  }
  if (azul) {
    clases = clases + " azul";
  }
  if (rojo) {
    clases = clases + " rojo";
  }
  if (amarillo) {
    clases = clases + " amarillo";
  }
  if (blanco) {
    clases = clases + " blanco";
  }
  if (ancho) {
    clases = clases + " ancho";
  }

  // Si es un botón de texto sin color especificado, aplicar el color principal
  const esTextoSinColor =
    (texto || icono) && !verde && !azul && !rojo && !amarillo && !negro && !blanco;
  const estiloTextoAzul = esTextoSinColor
    ? { color: "var(--principal)" }
    : {};

  function handleClick(event) {
    if (confirmacion) {
      setPopupConfirmacion(true);
      return;
    }
    if (uri) {
      if (uri.startsWith("/")) {
        if (nuevaPestana || event.metaKey || event.ctrlKey) {
          window.open(uri, "_blank");
        } else {
          navigate(uri);
        }
      } else {
        window.open(
          uri,
          nuevaPestana || event.metaKey || event.ctrlKey ? "_blank" : "_self"
        );
      }
    } else if (callback) {
      callback(event);
    }
  }

  // Si es un interruptor, renderizar InputSwitch
  if (interruptor) {
    return (
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.5em"
        }}
      >
        <InputSwitch
          checked={interruptorActivo}
          onChange={(e) => callback && callback(e.value)}
          disabled={disabled}
          style={{ flexShrink: 0 }}
        />
        {children && <span>{children}</span>}
      </div>
    );
  }

  // Si tiene menu, renderizar SplitButton
  if (menu && menu.length > 0) {
    // Transformar los items del menú al formato de PrimeReact
    const menuItems = menu.map((item) => ({
      label: item.etiqueta,
      icon: item.icono,
      command: (e) => {
        const event = e.originalEvent;
        if (item.uri) {
          if (item.uri.startsWith("/")) {
            if (event.metaKey || event.ctrlKey) {
              window.open(item.uri, "_blank");
            } else {
              navigate(item.uri);
            }
          } else {
            window.open(
              item.uri,
              event.metaKey || event.ctrlKey ? "_blank" : "_self"
            );
          }
        } else if (item.callback) {
          item.callback(event);
        }
      }
    }));

    return (
      <>
        <SplitButton
          label={children}
          css={estiloTextoAzul}
          onClick={(event) => {
            if (confirmacion) {
              setPopupConfirmacion(true);
              return;
            }

            if (uri) {
              if (uri.startsWith("/")) {
                if (event.metaKey || event.ctrlKey) {
                  window.open(uri, "_blank");
                } else {
                  navigate(uri);
                }
              } else {
                window.open(
                  uri,
                  event.metaKey || event.ctrlKey ? "_blank" : "_self"
                );
              }
            } else if (callback) {
              callback(event);
            }
          }}
          ref={boton}
          model={menuItems}
          className={clases}
          // Las clases de color (verde/azul/...) apuntan a .p-button; el
          // SplitButton pone className en el contenedor, así que se las pasamos
          // también a los botones internos (principal y desplegable).
          buttonClassName={clases}
          menuButtonClassName={clases}
          size="small"
          loading={loader}
          outlined={transparente}
          disabled={disabled}
          text={texto}
        />

        <ConfirmPopup
          // El ref del SplitButton no es un nodo del DOM: el elemento sale de
          // getElement()
          target={boton.current?.getElement?.()}
          visible={popupConfirmacion}
          onHide={() => setPopupConfirmacion(false)}
          message={confirmacion}
          icon="pi pi-exclamation-circle"
          accept={() => {
            if (uri) {
              if (uri.startsWith("/")) {
                navigate(uri);
              } else {
                window.location.href = uri;
              }
            } else if (callback) {
              callback();
            }
          }}
          rejectClassName="rojo transparente"
          rejectLabel="No"
          acceptClassName="verde transparente"
          acceptLabel="Si"
          acceptIcon="pi pi-check"
          rejectIcon="pi pi-times"
        />
      </>
    );
  }

  return (
    <>
      {badge ? (
        <span css={{ position: "relative", display: "inline-block" }}>
          <Button
            label={children}
            css={estiloTextoAzul}
            onClick={handleClick}
            ref={boton}
            text={texto || icono}
            rounded={icono}
            className={clases}
            size="small"
            loading={loader}
            outlined={transparente}
            disabled={disabled}
          />
          <span
            css={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              backgroundColor: "var(--rojo)",
              color: "white",
              borderRadius: "50%",
              minWidth: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              fontWeight: "700",
              lineHeight: 1,
              pointerEvents: "none"
            }}
          >
            {badge}
          </span>
        </span>
      ) : (
        <Button
          label={children}
          css={estiloTextoAzul}
          onClick={handleClick}
          ref={boton}
          text={texto || icono}
          rounded={icono}
          className={clases}
          size="small"
          loading={loader}
          outlined={transparente}
          disabled={disabled}
        />
      )}

      <ConfirmPopup
        target={boton.current}
        visible={popupConfirmacion}
        onHide={() => setPopupConfirmacion(false)}
        message={confirmacion}
        icon="pi pi-exclamation-circle"
        accept={() => {
          if (uri) {
            if (uri.startsWith("/")) {
              navigate(uri);
            } else {
              window.location.href = uri;
            }
          } else if (callback) {
            callback();
          }
        }}
        rejectClassName="rojo transparente"
        rejectLabel="No"
        acceptClassName="verde transparente"
        acceptLabel="Si"
        acceptIcon="pi pi-check"
        rejectIcon="pi pi-times"
      />
    </>
  );
}
